// Explore tab: the topic's summary + entry signals, then one card per explore
// track (goal, facts it fills, when to use / skip / stop) with its questions.
// On an introduction topic the tracks are life areas (IntroAreaFields).

import { useState } from "react";
import type { AreaSensitivity, ExploreTrack, TalkTrackContent, TrackQuestion } from "../../types/talkTrack";
import { ListField, NumberField, SelectField, TextField } from "./fields";
import { allUnitIds, nextUnitId } from "./format";
import { IntroAreaFields } from "./IntroAreaFields";
import { UnitCard } from "./UnitCard";
import { useUnitContext } from "./unitContext";

export interface IntroOptions {
  lifeAreas: string[];
  sensitivities: AreaSensitivity[];
  probeRungs: string[];
}

export function ExploreEditor({
  topicId,
  content,
  dimensions,
  onChange,
  intro,
}: {
  topicId: string;
  content: TalkTrackContent;
  dimensions: string[];
  onChange: (next: TalkTrackContent) => void;
  // Set on an introduction topic: its tracks are life areas.
  intro?: IntroOptions;
}) {
  const { editable, focusedUnit } = useUnitContext();
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  // Jumping to a question (from a check, comment or stats row) re-opens its
  // track if the author had collapsed it.
  const [seenFocus, setSeenFocus] = useState<string | null>(null);
  if (focusedUnit !== seenFocus) {
    setSeenFocus(focusedUnit);
    const owner = content.tracks.find((t) => t.questions.some((q) => q.id === focusedUnit));
    if (owner && collapsed.has(owner.id)) {
      const next = new Set(collapsed);
      next.delete(owner.id);
      setCollapsed(next);
    }
  }

  const setTracks = (tracks: ExploreTrack[]) => onChange({ ...content, tracks });
  const patchTrack = (i: number, patch: Partial<ExploreTrack>) =>
    setTracks(content.tracks.map((t, j) => (j === i ? { ...t, ...patch } : t)));
  const patchQuestion = (i: number, qi: number, patch: Partial<TrackQuestion>) =>
    patchTrack(i, {
      questions: content.tracks[i].questions.map((q, j) => (j === qi ? { ...q, ...patch } : q)),
    });

  function addTrack() {
    if (intro) {
      const used = new Set(content.tracks.map((t) => t.area));
      const area = intro.lifeAreas.find((a) => !used.has(a)) || intro.lifeAreas[0] || "anchor";
      const base = `${topicId}.${area}`;
      const id = allUnitIds(content).has(base) ? nextUnitId(content, `${base}-`, 2) : base;
      setTracks([
        ...content.tracks,
        {
          id, dimension: "context", name: "", goal: "", fills: [], useWhen: "", skipWhen: "", doneWhen: "",
          maxQuestions: 2, rationale: "", questions: [], area, sensitivity: "normal", permissionQuestion: null,
          bridges: [], probes: [], slots: [], shortForm: "",
        },
      ]);
      return;
    }
    const used = new Set(content.tracks.map((t) => t.dimension));
    const dimension = dimensions.find((d) => !used.has(d)) || dimensions[0] || "context";
    const base = `${topicId}.${dimension}`;
    const id = allUnitIds(content).has(base) ? nextUnitId(content, `${base}-`, 2) : base;
    setTracks([
      ...content.tracks,
      {
        id, dimension, name: "", goal: "", fills: [], useWhen: "", skipWhen: "", doneWhen: "",
        maxQuestions: 2, rationale: "", questions: [],
      },
    ]);
  }

  function removeTrack(i: number) {
    const t = content.tracks[i];
    if (t.questions.length && !confirm(`Remove track "${t.name || t.id}" and its ${t.questions.length} questions?`)) {
      return;
    }
    setTracks(content.tracks.filter((_, j) => j !== i));
  }

  function addQuestion(i: number) {
    const t = content.tracks[i];
    const id = nextUnitId(content, `${t.id}.q`, t.questions.length + 1);
    patchTrack(i, { questions: [...t.questions, { id, text: "", voiceText: "" }] });
  }

  function toggleCollapsed(id: string) {
    setCollapsed((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  return (
    <>
      <fieldset>
        <legend>{intro ? "Introduction" : "Topic playbook"}</legend>
        <TextField label="Summary (what this talk track is for)" editable={editable} multiline rows={3}
          value={content.summary} onChange={(summary) => onChange({ ...content, summary })} />
        {!intro && (
          <ListField label="Entry signals (what the user says that routes a conversation here)" editable={editable}
            value={content.entrySignals} rows={4}
            onChange={(entrySignals) => onChange({ ...content, entrySignals })} />
        )}
      </fieldset>

      {content.tracks.length === 0 && (
        <div className="state-box">No explore tracks yet{editable ? " — add one below." : "."}</div>
      )}

      {content.tracks.map((t, i) => {
        const expanded = !collapsed.has(t.id);
        return (
          <UnitCard key={t.id} unitId={t.id} kind={intro ? "Area" : "Track"} title={t.name}
            onRemove={() => removeTrack(i)}>
            {intro ? (
              <>
                <div className="row-3">
                  <TextField label="Name" editable={editable} value={t.name}
                    onChange={(name) => patchTrack(i, { name })} />
                  <NumberField label="Max questions" editable={editable} value={t.maxQuestions} min={1} max={6}
                    onChange={(maxQuestions) => patchTrack(i, { maxQuestions })} />
                  <TextField label="Done when" editable={editable} value={t.doneWhen}
                    onChange={(doneWhen) => patchTrack(i, { doneWhen })} />
                </div>
                <TextField label="Goal (what Aura wants to understand)" editable={editable} multiline
                  value={t.goal} onChange={(goal) => patchTrack(i, { goal })} />
                <IntroAreaFields track={t} content={content} editable={editable} {...intro}
                  onPatch={(patch) => patchTrack(i, patch)} />
              </>
            ) : (
            <>
            <div className="row-3">
              <TextField label="Name" editable={editable} value={t.name}
                onChange={(name) => patchTrack(i, { name })} />
              <SelectField label="Dimension" editable={editable} value={t.dimension} options={dimensions}
                onChange={(dimension) => patchTrack(i, { dimension })} />
              <NumberField label="Max questions" editable={editable} value={t.maxQuestions} min={1} max={6}
                onChange={(maxQuestions) => patchTrack(i, { maxQuestions })} />
            </div>
            <TextField label="Goal (what the guide is trying to learn)" editable={editable} multiline
              value={t.goal} onChange={(goal) => patchTrack(i, { goal })} />
            <ListField label="Facts it fills (slot names)" editable={editable} value={t.fills} rows={2}
              onChange={(fills) => patchTrack(i, { fills })} />
            <div className="row-3">
              <TextField label="Use when" editable={editable} multiline value={t.useWhen}
                onChange={(useWhen) => patchTrack(i, { useWhen })} />
              <TextField label="Skip when" editable={editable} multiline value={t.skipWhen}
                onChange={(skipWhen) => patchTrack(i, { skipWhen })} />
              <TextField label="Done when" editable={editable} multiline value={t.doneWhen}
                onChange={(doneWhen) => patchTrack(i, { doneWhen })} />
            </div>
            <TextField label="Rationale (guide-facing, never spoken)" editable={editable} multiline rows={3}
              value={t.rationale} onChange={(rationale) => patchTrack(i, { rationale })} />

            <div className="tt-subhead">
              <span>Questions ({t.questions.length})</span>
              <button className="btn btn-sm btn-ghost" onClick={() => toggleCollapsed(t.id)} aria-expanded={expanded}>
                {expanded ? "Hide" : "Show"}
              </button>
            </div>
            {expanded && (
              <div className="tt-units">
                {t.questions.map((q, qi) => (
                  <UnitCard key={q.id} unitId={q.id} kind="Question" title={q.text}
                    onApplySuggestion={(text) => patchQuestion(i, qi, { text })}
                    onRemove={() => patchTrack(i, { questions: t.questions.filter((_, j) => j !== qi) })}>
                    <TextField label="Wording (what the guide uses)" editable={editable} multiline value={q.text}
                      onChange={(text) => patchQuestion(i, qi, { text })} />
                    <TextField label="Short spoken version (used when the reply is spoken)" editable={editable}
                      value={q.voiceText} onChange={(voiceText) => patchQuestion(i, qi, { voiceText })} />
                  </UnitCard>
                ))}
                {editable && (
                  <button className="btn btn-sm" onClick={() => addQuestion(i)}>+ question</button>
                )}
              </div>
            )}
            </>
            )}
          </UnitCard>
        );
      })}

      {editable && (
        <button className="btn" onClick={addTrack}>{intro ? "+ Life area" : "+ Explore track"}</button>
      )}
    </>
  );
}
